import React, { useState, useEffect, useRef } from 'react';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  Stack,
  FormHelperText,
  Tabs,
  Tab,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import { noticesApi } from '../../api/notices';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import PageLayout from '../../components/layout/PageLayout';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import ProgressBar from '../../components/common/ProgressBar';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import CustomTextField from '../../components/common/CustomTextField';
import RepresentativeImageSelector, {
  getRepresentativeImageUrl,
} from '../../components/common/RepresentativeImageSelector';
import { useUserStore } from '../../stores/userStore';
import useAdminDraftGuard, { AdminDraftProvider } from '../admin/useAdminDraftGuard';

const schema = yup.object().shape({
  title: yup.string().trim().required('제목을 입력하세요.'),
  content: yup
    .string()
    .required('설명을 입력하세요.')
    .test('body', '설명을 입력하세요.', (value) => {
      const body = new DOMParser().parseFromString(value || '', 'text/html').body;
      return Boolean(
        body.textContent.replace(/\u00a0/g, ' ').trim() || body.querySelector('img[src]'),
      );
    }),
});

const NoticeForm = () => {
  const theme = useTheme();
  const live = useRef(true);
  const pending = useRef(false);
  const uploadPending = useRef(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const { id } = useParams();
  const isEditMode = !!id;
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const returnPage = searchParams.get('returnPage');

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      titleEn: '',
      content: '',
      contentEn: '',
    },
  });

  const navigate = useNavigate();
  const content = watch('content');
  const [representativeImageUrl, setRepresentativeImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [editorLocale, setEditorLocale] = useState('ko');
  const locked = saving || isSubmitting || uploading || loadFailed;
  const draft = useAdminDraftGuard(
    { fields: watch(), representativeImageUrl },
    {
      enabled: !isLoading && !loadFailed,
      busy: saving || isSubmitting || uploading,
    },
  );
  const uploadChanged = (next) => {
    if (!live.current) return;
    uploadPending.current = next;
    setUploading(next);
  };

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    let active = true;
    if (isEditMode) {
      setIsLoading(true);
      setLoadFailed(false);
      const fetchNotice = async () => {
        try {
          const response = await noticesApi.getNoticeById(id);
          if (!active || !live.current) return;
          const notice = response.data;
          if (
            !notice ||
            String(notice.id) !== id ||
            typeof notice.title !== 'string' ||
            typeof notice.content !== 'string'
          )
            throw new Error('Invalid notice response');

          setValue('title', notice.title);
          setValue('titleEn', notice.titleEn || '');
          setValue('content', notice.content);
          setValue('contentEn', notice.contentEn || '');

          if (notice.imageUrl) {
            setRepresentativeImageUrl(notice.imageUrl);
          }
        } catch (error) {
          if (active && live.current) setLoadFailed(true);
        } finally {
          if (active && live.current) setIsLoading(false);
        }
      };

      fetchNotice();
    }
    return () => {
      active = false;
    };
  }, [id, isEditMode, setValue, reloadKey]);

  const onSubmit = async (formData) => {
    if (!live.current || pending.current || uploadPending.current || isLoading || loadFailed)
      return;
    pending.current = true;
    setSaving(true);
    try {
      const imageUrl =
        representativeImageUrl || getRepresentativeImageUrl(formData.content, '', '');
      let destination = id;
      if (isEditMode) {
        await noticesApi.updateNotice(id, { ...formData, imageUrl });
      } else {
        const response = await noticesApi.createNotice({ ...formData, imageUrl });
        if (!live.current) return;
        const created = response?.data;
        destination =
          typeof created?.slug === 'string' && created.slug.trim()
            ? created.slug
            : Number.isSafeInteger(Number(created?.id)) && Number(created?.id) > 0
              ? String(created.id)
              : null;
        if (!destination) throw new Error('Unknown create result');
      }
      if (!live.current) return;
      draft.markClean();
      navigate(`/notice/${encodeURIComponent(destination)}`);
    } catch (error) {
      if (live.current)
        setError('root.server', {
          message:
            '저장하지 못했거나 결과를 확인하지 못했습니다. 입력 내용은 유지됩니다. 재시도 전 공지 목록에서 저장 여부를 확인해주세요.',
        });
    } finally {
      pending.current = false;
      if (live.current) setSaving(false);
    }
  };

  const handleCancel = () => {
    if (pending.current || uploadPending.current) return;
    draft.markClean();
    if (from === 'detail' && isEditMode && !loadFailed) {
      navigate(`/notice/${encodeURIComponent(id)}`);
    } else if (from === 'list' && /^[1-9]\d*$/.test(returnPage || '')) {
      navigate(`/notice?page=${returnPage}`);
    } else if (isEditMode && !loadFailed) {
      navigate(`/notice/${encodeURIComponent(id)}`);
    } else {
      navigate('/notice');
    }
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box
          sx={{
            maxWidth: '780px',
            mx: 'auto',
            minWidth: 0,
            overflowWrap: 'anywhere',
            '& .MuiTextField-root': { minWidth: 0 },
            '& .MuiAlert-root': { flexWrap: 'wrap' },
            '& .MuiAlert-message': { minWidth: 0, flex: '1 1 110px' },
          }}
        >
          {loadFailed && (
            <Alert
              severity="error"
              action={<Button onClick={() => setReloadKey((v) => v + 1)}>재시도</Button>}
            >
              공지를 불러오지 못했습니다. 다시 조회하거나 목록으로 돌아가주세요.
            </Alert>
          )}
          {(saving || uploading) && (
            <Typography role="status">
              {uploading ? '이미지 업로드가 끝날 때까지 기다려주세요.' : '공지를 저장하는 중…'}
            </Typography>
          )}
          <Stack
            component="fieldset"
            disabled={locked}
            inert={locked ? '' : undefined}
            spacing={'30px'}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  제목
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  inputProps={{ 'aria-label': '제목' }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  영문 제목 (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('titleEn')}
                  inputProps={{ 'aria-label': '영문 제목' }}
                  placeholder="영문 제목이 없으면 한국어 제목을 표시합니다."
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  본문
                </Typography>
                <Tabs
                  variant="scrollable"
                  scrollButtons="auto"
                  value={editorLocale}
                  onChange={(_, nextLocale) => setEditorLocale(nextLocale)}
                  aria-label="공지 본문 언어"
                >
                  <Tab value="ko" label="한국어" />
                  <Tab value="en" label="영어 (선택)" />
                </Tabs>
                <Controller
                  key={editorLocale}
                  name={editorLocale === 'ko' ? 'content' : 'contentEn'}
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        minWidth: 0,
                        '& > .MuiBox-root': { minWidth: 0 },
                        '& .ql-toolbar, & .ql-container': {
                          borderColor: 'divider',
                          color: 'text.primary',
                        },
                        '& .ql-editor': { overflowWrap: 'anywhere' },
                        '& .ql-editor.ql-blank::before': { color: 'text.secondary' },
                        '& .ql-toolbar .ql-stroke': { stroke: theme.palette.text.primary },
                        '& .ql-toolbar .ql-fill': { fill: theme.palette.text.primary },
                        '& .ql-picker-label': { color: 'text.primary' },
                        '& .ql-picker-options, & .ql-tooltip': {
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                        },
                      }}
                    >
                      <ReactQuillEditor
                        value={field.value}
                        onChange={field.onChange}
                        readOnly={saving || isSubmitting || loadFailed}
                        onUploadPendingChange={uploadChanged}
                      />
                    </Box>
                  )}
                />
                <RepresentativeImageSelector
                  autoSelect={false}
                  disabled={locked}
                  content={content}
                  value={representativeImageUrl}
                  onChange={setRepresentativeImageUrl}
                />
                {errors.content && (
                  <FormHelperText
                    error
                    sx={{
                      textAlign: 'left',
                      margin: '4px 0 0 0',
                      fontSize: '12px',
                      color: theme.palette.red,
                    }}
                  >
                    {errors.content.message}
                  </FormHelperText>
                )}
              </Stack>
            </Stack>

            {/* 버튼 */}
            {errors.root?.server && (
              <FormHelperText error>{errors.root.server.message}</FormHelperText>
            )}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width="100%" maxWidth={420} flexWrap="wrap">
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={() => (draft.dirty ? setIsCancelDialogOpen(true) : handleCancel())}
                  sx={{ flex: '1 1 100px', minWidth: 0, minHeight: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={locked}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ flex: '1 1 100px', minWidth: 0, minHeight: '48px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </BasicButton>
              </Box>
            </Box>
          </Stack>
          {loadFailed && <Button onClick={handleCancel}>목록으로 돌아가기</Button>}
        </Box>
      </ProgressBar>
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancel}
      />
    </PageLayout>
  );
};

export default function GuardedNoticeForm() {
  const { id } = useParams();
  const user = useUserStore((state) => state.user);
  return (
    <AdminDraftProvider>
      <NoticeForm key={JSON.stringify([id, user?.id, user?.role])} />
    </AdminDraftProvider>
  );
}
